import React, { useCallback } from 'react';
import {
  bool, func, string, arrayOf, number,
} from 'prop-types';
import styled, { css } from 'styled-components';
import { DatePicker, TimePicker } from '@material-ui/pickers';

import {
  Button, Flex, Typography, Icon, Modal, Input,
} from '@atoms';
import { DeleteModal, AvailabilityDays } from '@molecules';
import { ReactComponent as Edit } from '@assets/edit.svg';
import { ReactComponent as Remove } from '@assets/remove-item.svg';

const AvailabilityProcessModal = ({
  onStartDateRemove,
  onEndDateRemove,
  onStartTimeRemove,
  onEndTimeRemove,
  isConfirmButtonDisabled,
  startTime,
  endTime,
  startDate,
  endDate,
  onStartTimeChange,
  onEndTimeChange,
  onStartDateChange,
  onEndDateChange,
  selectedWeekDays,
  onDayClick,
  onModalClose,
  onAvailabilityNameChange,
  isEditing,
  onAvailabilityEditSubmit,
  availabilityName,
  isModalOpen,
  onAvailabilityDeleteModalChangeState,
  isAvailabilityDeleteModalOpen,
  onAvailabilityDelete,
}) => {
  const handleStartDateChange = useCallback(date => {
    // set time to 00:00 for a start date object
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    onStartDateChange(date);
  }, [onStartDateChange]);

  const handleEndDateChange = useCallback(date => {
    // set time to 23:59 for an end date object
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(0);
    date.setMilliseconds(0);
    onEndDateChange(date);
  }, [onEndDateChange]);

  return (
    <Modal
      isOpen={isModalOpen}
      ariaHideApp={false}
      onRequestClose={onModalClose}
      shouldCloseOnOverlayClick
    >
      <InputWrapper marginBottom="xl3">
        <Flex>
          <Icon SVG={Edit} width="16px" height="16px" />
        </Flex>
        <Input
          as="textarea"
          marginTop="xl2"
          type="text"
          id="availabilityName"
          name="availabilityName"
          placeholder="Availability period name"
          value={availabilityName}
          onChange={onAvailabilityNameChange}
          data-cy="availabilityName"
        />
      </InputWrapper>
      <AvailabilityDays onDayClick={onDayClick} selectedWeekDays={selectedWeekDays} />
      <Flex marginTop="xl3" justifyBetween>
        <DateWrapper itemsStart column>
          <Typography weight="bold">Time starts on</Typography>
          <TimePicker minutesStep={15} onChange={onStartTimeChange} value={startTime || null} />
          {startTime && (
            <RemoveButton onClick={onStartTimeRemove}>
              <Icon SVG={Remove} width="14px" height="14px" />
            </RemoveButton>
          )}
        </DateWrapper>
        <DateWrapper itemsStart column>
          <Typography weight="bold">Time ends on</Typography>
          <TimePicker minutesStep={15} onChange={onEndTimeChange} value={endTime || null} />
          {endTime && (
            <RemoveButton onClick={onEndTimeRemove}>
              <Icon SVG={Remove} width="14px" height="14px" />
            </RemoveButton>
          )}
        </DateWrapper>
      </Flex>
      <Flex marginTop="xl3" justifyBetween>
        <DateWrapper itemsStart column>
          <Typography weight="bold">Date starts on</Typography>
          <DatePicker onChange={handleStartDateChange} value={startDate || null} />
          {startDate && (
            <RemoveButton onClick={onStartDateRemove}>
              <Icon SVG={Remove} width="14px" height="14px" />
            </RemoveButton>
          )}
        </DateWrapper>
        <DateWrapper itemsStart column>
          <Typography weight="bold">Date ends on</Typography>
          <DatePicker onChange={handleEndDateChange} value={endDate || null} />
          {endDate && (
            <RemoveButton onClick={onEndDateRemove}>
              <Icon SVG={Remove} width="14px" height="14px" />
            </RemoveButton>
          )}
        </DateWrapper>
      </Flex>
      {isEditing && (
        <Button.Rectangular
          marginTop="xl"
          onClick={onAvailabilityDeleteModalChangeState}
          primary
          padding="lg"
        >
          Delete
        </Button.Rectangular>
      )}
      <Flex>
        <Button.Rectangular
          fullWidth
          primaryInverted
          padding="md"
          onClick={onModalClose}
          marginTop="xl5"
        >
          <Typography size="lg">
            Cancel
          </Typography>
        </Button.Rectangular>
        <Button.Rectangular
          fullWidth
          primary
          padding="md"
          marginLeft="xl"
          onClick={onAvailabilityEditSubmit}
          marginTop="xl5"
          disabled={isConfirmButtonDisabled}
        >
          <Typography size="lg">
            Save
          </Typography>
        </Button.Rectangular>
      </Flex>
      <DeleteModal
        name={availabilityName}
        isModalOpen={isAvailabilityDeleteModalOpen}
        onModalClose={onAvailabilityDeleteModalChangeState}
        onDelete={onAvailabilityDelete}
      />
    </Modal>
  );
};

const RemoveButton = styled(Flex)`
  ${({ theme: { color } }) => css`
    position: absolute;
    right: 0;
    bottom: 8px;

    ${Icon} {
      path {
        fill: ${color.stone};
      }
    }
  `}
`;

const DateWrapper = styled(Flex)`
  max-width: 120px;
  position: relative;
`;

const InputWrapper = styled(Flex)`
  ${({ theme: { color, typography, spacing } }) => css`
    width: 100%;
    position: relative;
    padding: ${spacing.none} ${spacing.xl};

    ${Flex} {
      position: absolute;
      bottom: 8px;
      right: 15px;

      ${Icon} {
        path {
          fill: ${color.black};
        }
      }
    }

    ${Input} {
        height: 100%;
        border: none;
        border-radius: 0px;
        text-align: left;
        border-bottom: 1px solid ${color.black};
        font-weight: ${typography.weight.medium};
        font-size: ${typography.size.xl};
        padding: ${spacing.xs};
        padding-right: ${spacing.xl3};

        resize: none;

        height: 32px;
        -ms-overflow-style: none;
        scrollbar-width: none;
        ::-webkit-scrollbar {
          display: none;
        }

        &::placeholder {
          color: ${color.dust};
        }
    }
  `}
`;

AvailabilityProcessModal.propTypes = {
  onStartDateRemove: func.isRequired,
  onEndDateRemove: func.isRequired,
  onStartTimeRemove: func.isRequired,
  onEndTimeRemove: func.isRequired,
  isConfirmButtonDisabled: bool.isRequired,
  startTime: string.isRequired,
  endTime: string.isRequired,
  startDate: string.isRequired,
  endDate: string.isRequired,
  onStartTimeChange: func.isRequired,
  onEndTimeChange: func.isRequired,
  onStartDateChange: func.isRequired,
  onEndDateChange: func.isRequired,
  selectedWeekDays: arrayOf(number).isRequired,
  onDayClick: func.isRequired,
  onAvailabilityDeleteModalChangeState: func.isRequired,
  isAvailabilityDeleteModalOpen: bool.isRequired,
  onAvailabilityDelete: func.isRequired,
  onAvailabilityEditSubmit: func.isRequired,
  isEditing: bool.isRequired,
  availabilityName: string.isRequired,
  onAvailabilityNameChange: func.isRequired,
  onModalClose: func.isRequired,
  isModalOpen: bool.isRequired,
};

export default AvailabilityProcessModal;
